// ─────────────────────────────────────────────────────────────────────────────
// Talkback to field reporters (studio return feed, FieldReportersPanel.jsx).
//
// arm()    opens the microphone once (one permission prompt; the browser's mic
//          indicator stays on while armed) and the /return-talk socket.
// setTalking(true|false)
//          sends the microphone only while true. Pressing "talk" therefore costs
//          no start-up: the first word reaches the reporter ~150 ms later.
// disarm() closes both.
// setTarget(n | "all")
//          which reporter hears it (field-reporter channels): one channel, or
//          every live one. Default: channel 1. Kept across reconnects.
//
// The microphone goes out as raw 20 ms PCM packets (public/pcm-worklet.js).
// The access token is sent as the socket's first message, never in the URL.
// ─────────────────────────────────────────────────────────────────────────────

const WORKLET_URL = `${process.env.PUBLIC_URL || ""}/pcm-worklet.js`;

export function createTalkback({ onState, onLevel }) {
  let ctx = null;
  let stream = null;
  let node = null;
  let ws = null;
  let armed = false;
  let talking = false;
  let reconnectTimer = null;
  let state = "off";
  let target = 1;
  let authed = false;

  const sendTarget = () => {
    if (ws && authed && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "target", channel: target }));
  };

  const setState = (next, message = "") => {
    state = next;
    onState?.(next, message);
  };

  function connect() {
    if (!armed) return;
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${proto}//${window.location.host}/return-talk`);
    socket.binaryType = "arraybuffer";
    ws = socket;
    authed = false;
    socket.onopen = () => {
      socket.send(JSON.stringify({ type: "auth", token: localStorage.getItem("token") || "" }));
    };
    socket.onmessage = (e) => {
      let msg = null;
      try { msg = JSON.parse(e.data); } catch { return; }
      if (msg.type === "ready") { authed = true; sendTarget(); setState("ready"); }
      else if (msg.type === "error") setState("error", msg.message || "Talkback refused");
    };
    socket.onclose = (e) => {
      if (ws !== socket) return;
      ws = null;
      if (!armed) return;
      if (e.code === 4003) { setState("error", "Talkback is locked during the recovery restriction."); return; }
      // Dropped (network, server restart, an expired sign-in): try again. The
      // token is read afresh each time, so a renewed sign-in is picked up.
      setState("connecting");
      reconnectTimer = setTimeout(connect, e.code === 4001 ? 5000 : 1500);
    };
  }

  async function arm() {
    if (armed) return;
    armed = true;
    setState("arming");
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      try {
        ctx = new AudioContext({ sampleRate: 48000, latencyHint: "interactive" });
      } catch {
        ctx = new AudioContext({ latencyHint: "interactive" });   // the worklet resamples
      }
      await ctx.audioWorklet.addModule(WORKLET_URL);
      if (!armed) return;
      const source = ctx.createMediaStreamSource(stream);
      node = new AudioWorkletNode(ctx, "talkback-tap", { numberOfInputs: 1, numberOfOutputs: 0, channelCount: 1 });
      node.port.onmessage = ({ data }) => {
        onLevel?.(data.peak);
        if (talking && ws && ws.readyState === WebSocket.OPEN) ws.send(data.pcm);
      };
      source.connect(node);
      if (ctx.state === "suspended") await ctx.resume();
      setState("connecting");
      connect();
    } catch (err) {
      const denied = err && (err.name === "NotAllowedError" || err.name === "SecurityError");
      disarm();
      setState("error", denied
        ? "The browser blocked the microphone. Allow it for this site, then try again."
        : `Could not open the microphone (${err?.message || "unknown error"}).`);
    }
  }

  function setTalking(on) {
    talking = !!on && armed;
  }

  function setTarget(next) {
    if (next === target) return;
    target = next;
    sendTarget();
  }

  function disarm() {
    armed = false;
    talking = false;
    clearTimeout(reconnectTimer);
    try { ws?.close(); } catch { /* closed */ }
    ws = null;
    try { node?.disconnect(); } catch { /* gone */ }
    node = null;
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    try { ctx?.close(); } catch { /* closed */ }
    ctx = null;
    onLevel?.(0);
    if (state !== "error") setState("off");
  }

  return { arm, disarm, setTalking, setTarget, get state() { return state; } };
}
